import React, { useState } from "react";
import { TspBadge } from "../../../../components/primitives";
import { RESPONSE_RATINGS, RESPONSE_RATING_LABEL, RESPONSE_RATING_TONE } from "../../../../constants/tspOperations";
import SectionShell, { FormButtons, SectionForm, autoVisitNote } from "./SectionShell";

const REMARKS_MAX = 2000;

// Public Response: how people received the team at this place (Excellent /
// Very Good / Good / Poor) with remarks, filed under the place's visit (the
// first save records the visit when there is none yet).
// store.responses = [{ id, rating, remarks }].
export default function PublicResponseSection({ idPrefix, place, store, canManage, disabled }) {
  return (
    <SectionShell
      idPrefix={idPrefix}
      title="Public Response"
      addLabel="Add Public Response"
      emptyText="No public response recorded for this place yet."
      items={store.responses}
      max={store.responsesMax || Infinity}
      loading={store.responsesLoading}
      error={store.responsesError}
      onRetry={store.reloadResponses}
      canAdd={canManage}
      canEdit={() => canManage}
      canRemove={() => canManage}
      onAdd={store.addResponse}
      onUpdate={store.updateResponse}
      onRemove={store.removeResponse}
      confirmRemove="Remove this public response?"
      removeLabel={(item) => `Remove the ${RESPONSE_RATING_LABEL[item.rating] || item.rating} response`}
      disabled={disabled}
      renderItem={(item) => (
        <span className="tsp-pc-line">
          <TspBadge tone={RESPONSE_RATING_TONE[item.rating] || "amber"}>{RESPONSE_RATING_LABEL[item.rating] || item.rating}</TspBadge>
          {item.remarks && <span className="tsp-pc-remarks">{item.remarks}</span>}
        </span>
      )}
      renderForm={(form) => <ResponseForm {...form} />}
    />
  );
}

function ResponseForm({ initial, submit, cancel, busy, idPrefix }) {
  const [rating, setRating] = useState(initial ? initial.rating : "");
  const [remarks, setRemarks] = useState(initial ? initial.remarks || "" : "");
  const [error, setError] = useState("");

  const onSubmit = () => {
    if (!rating) {
      setError("Choose a rating.");
      return;
    }
    if (remarks.trim().length > REMARKS_MAX) {
      setError(`Remarks must be ${REMARKS_MAX} characters or fewer.`);
      return;
    }
    submit({ rating, remarks: remarks.trim() });
  };

  return (
    <SectionForm onSubmit={onSubmit}>
      <span className="tsp-field-label" id={`${idPrefix}-rating-label`}>
        Public Response Rating <span className="tsp-required" aria-hidden="true">*</span>
      </span>
      <div className="tsp-aj-ratings" role="radiogroup" aria-labelledby={`${idPrefix}-rating-label`}>
        {RESPONSE_RATINGS.map((option) => {
          const selected = rating === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`tsp-aj-rating ${selected ? "selected" : ""}`}
              style={selected ? { borderColor: option.color, color: option.color } : undefined}
              disabled={busy}
              onClick={() => {
                setRating(option.value);
                setError("");
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      <div className="tsp-form-field" style={{ marginTop: 12 }}>
        <label htmlFor={`${idPrefix}-remarks`}>Remarks & Public Feedback (Optional)</label>
        <input id={`${idPrefix}-remarks`} type="text" className="tsp-input" placeholder="e.g. Enthusiastic crowd at the market" value={remarks} disabled={busy} onChange={(event) => setRemarks(event.target.value)} />
      </div>
      {error && (
        <p className="tsp-form-error" role="alert">
          {error}
        </p>
      )}
      <FormButtons busy={busy} onSave={onSubmit} onCancel={cancel} saveLabel="Save Public Response" />
    </SectionForm>
  );
}
